'use client';

import cn from 'classnames';
import { useId } from 'react';
import { IconVideoPlayButton } from '@/assets/icons/IconVideoPlayButton';
import { IconVideoPauseButton } from '@/assets/icons/IconVideoPauseButton';
import { IconReplayButton } from '@/assets/icons/IconReplayButton';
import { useAudioPlayer } from '@/hooks/useAudioPlayer';
import styles from './audioPlayer.module.scss';

export interface AudioPlayerProps {
    source: string;
    name?: string;
    isActive: boolean;
    onActivate: (isActive: boolean) => void;
}

export function AudioPlayer({ source, name = '', isActive, onActivate }: AudioPlayerProps): React.JSX.Element {
    const {
        setAudioElementRef,
        isEnded,
        duration,
        progressPercent,
        time,
        showPlayButton,
        showPauseButton,
        formatTime,
        handlePlayPause,
        handleReplay,
        handleScrub,
        handleAudioEnded,
        handleTimeUpdate,
        handleLoadedMetadata,
    } = useAudioPlayer({ source, isActive, onActivate, playEventBubbles: true });

    const inputId = useId();

    const analytics: DataLayerInfo = {
        audioInfo: {
            name: name || source.split('/').pop() || '',
        },
    };

    const formattedTime = formatTime(Math.floor(time.currentTime));

    return (
        <div className={cn(styles.audioContainer, { [styles.active]: isActive })}>
            {/* eslint-disable-next-line jsx-a11y/media-has-caption -- Captions require a CMS-provided VTT file; audio elements are better served by a transcript alternative. */}
            <audio
                src={source}
                ref={setAudioElementRef}
                onEnded={handleAudioEnded}
                onLoadedMetadata={handleLoadedMetadata}
                onTimeUpdate={handleTimeUpdate}
                data-audioplayer={analytics.audioInfo.name}
            />
            <div className={cn(styles.controls, { [styles.active]: isActive })}>
                {showPlayButton && (
                    <button onClick={handlePlayPause} aria-label={`play ${name}`} className={styles.playButton}>
                        <IconVideoPlayButton />
                    </button>
                )}
                {showPauseButton && (
                    <button onClick={handlePlayPause} aria-label={`pause ${name}`}>
                        <IconVideoPauseButton />
                    </button>
                )}
                {isEnded && (
                    <button onClick={handleReplay} aria-label={`replay ${name}`}>
                        <IconReplayButton />
                    </button>
                )}

                {isActive && (
                    <div className={styles.slideAnimation}>
                        <time
                            className={cn(styles.timeEllapsed, styles.lightTextColor)}
                            aria-label={`time ellapsed ${formattedTime}`}
                        >
                            {formattedTime}
                        </time>
                        <div className={styles.progressBarWrapper}>
                            <input
                                type="range"
                                id={inputId}
                                min={0}
                                max={duration}
                                step="any"
                                value={time.currentTime}
                                onChange={handleScrub}
                                aria-valuetext="Audio bar slider"
                                aria-label="Audio bar slider"
                            />
                            <div
                                className={cn(styles.progressBar)}
                                style={{
                                    background: `linear-gradient(to right, var(--range-progress-bar-ellapsed) ${progressPercent}%, var(--range-progress-bar-remaining) ${progressPercent}%)`,
                                }}
                            />
                        </div>
                        <time
                            className={cn(styles.timeRemaining, styles.timeRemainingIsActive, styles.lightTextColor)}
                            aria-label={`time remaining ${formatTime(time.timeRemaining)}`}
                        >
                            {formatTime(time.timeRemaining)}
                        </time>
                    </div>
                )}

                {!isActive && (
                    <time
                        className={cn(styles.timeRemaining, styles.timeRemainingNotActive)}
                        aria-label={`audio duration ${formatTime(duration)}`}
                    >
                        {formatTime(duration)}
                    </time>
                )}
            </div>
        </div>
    );
}
